import { Message, SlideOut } from '@sd/prism';
import {
  AccountGroupWithAccounts,
  availableAccountGroupsState,
  canDebugStuffState,
  SDUserAccount,
  userPreferencesState
} from '@sd/shared';
import { useEffect, useState } from 'react';
import { Accordion, Button, Card, Form } from 'react-bootstrap';
import { useRecoilState, useRecoilValue } from 'recoil';
import styled from 'styled-components';

export const AccountGroupSelector = () => {
  const canDebugStuff = useRecoilValue(canDebugStuffState);
  const availableAccountGroups = useRecoilValue(availableAccountGroupsState);
  const availableAccounts = availableAccountGroups.flatMap(ag => ag.accounts);
  const [userPreferences, setUserPreferences] = useRecoilState(userPreferencesState);
  const [showSelector, setShowSelector] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const deselectedAccounts =
    userPreferences?.deselectedAccounts?.flatMap(guid => availableAccounts.find(a => a.guid === guid) || []) || [];
  const selectedAccountGroup = availableAccountGroups.find(g => userPreferences!.accountGroup === g.id);
  const displaySelectMsg = selectedAccountGroup?.accounts.every(acct =>
    userPreferences?.deselectedAccounts?.includes(acct.guid)
  );

  useEffect(() => {
    if (selectedAccountGroup && displaySelectMsg) {
      handleAccountGroupChange(selectedAccountGroup);
    }
    if (!selectedAccountGroup && availableAccountGroups[0]) {
      handleAccountGroupChange(availableAccountGroups[0]);
    }
  }, []);

  const handleAccountChange = (account: SDUserAccount) => {
    if (deselectedAccounts.some(a => a.guid === account.guid)) {
      // Selected, remove it from the deselected list
      setUserPreferences({
        ...userPreferences!,
        deselectedAccounts: [...deselectedAccounts.filter(a => a.guid !== account.guid)].map(a => a.guid)
      });
    } else {
      if ((selectedAccountGroup?.accounts || []).length - deselectedAccounts.length === 1) {
        return setErrorMessage('At least one account must be selected');
      }
      // Deselected, add it to the deselected list
      setUserPreferences({
        ...userPreferences!,
        deselectedAccounts: [...deselectedAccounts, account].map(a => a.guid)
      });
    }
    setErrorMessage('');
  };

  const handleAccountGroupChange = (accountGroup: AccountGroupWithAccounts) => {
    if (accountGroup.id === selectedAccountGroup?.id) {
      if (deselectedAccounts.length > 0) {
        // Select all in group
        setUserPreferences({ ...userPreferences!, accountGroup: accountGroup.id, deselectedAccounts: [] });
        setErrorMessage('');
      } else {
        setErrorMessage('At least one account must be selected');
      }
    } else {
      setErrorMessage('');
      setUserPreferences({ ...userPreferences!, accountGroup: accountGroup.id, deselectedAccounts: [] });
    }
  };

  return (
    <div className="d-flex align-items-center">
      <span data-testid="display-select-msg">{displaySelectMsg && 'Please select an account'}</span>
      <span data-testid="selected-acct-group">{!displaySelectMsg && selectedAccountGroup?.name}</span>
      <Button variant="link" size="sm" onClick={() => setShowSelector(true)}>
        Switch Account
      </Button>
      <SlideOut actionable open={showSelector} onCloseButtonClick={() => setShowSelector(false)}>
        <div className="d-flex justify-content-start flex-column h-100" style={{ minWidth: '50vw' }}>
          <SlideOut.Header>
            <h1>Select Accounts</h1>
          </SlideOut.Header>
          {errorMessage && (
            <Message color="danger" className="mt-0 mb-3 mx-0" data-testid="account-message">
              {errorMessage}
            </Message>
          )}
          {userPreferences && (
            <CardWrapper>
              <Accordion activeKey={selectedAccountGroup?.id.toString()}>
                {availableAccountGroups.map(accountGroup => (
                  <Card key={accountGroup.id.toString()}>
                    <Card.Header>
                      <Form.Check type="checkbox" id={`check-account-group-${accountGroup.name}`}>
                        <Form.Check.Input
                          data-testid={`account-checkbox-${accountGroup.name}`}
                          type="checkbox"
                          onChange={() => handleAccountGroupChange(accountGroup)}
                          checked={accountGroup.id === selectedAccountGroup?.id}
                        />
                        <Form.Check.Label className="d-flex justify-content-between">
                          {accountGroup.name}
                        </Form.Check.Label>
                      </Form.Check>
                    </Card.Header>
                    <Accordion.Collapse eventKey={accountGroup.id.toString()}>
                      <IndentedCardBody>
                        {accountGroup.accounts.map(account => (
                          <Form.Check
                            key={account.guid}
                            id={`check-seller-${account.guid}`}
                            label={account.name}
                            onChange={() => handleAccountChange(account)}
                            checked={!deselectedAccounts.some(a => a.guid === account.guid)}
                            title={
                              canDebugStuff
                                ? `⚙ Debug Notes\n----------\nguid: ${
                                    account.guid
                                  }\nsellerNumbers: ${account.sellerNumbers.join(', ')}`
                                : undefined
                            }
                          />
                        ))}
                      </IndentedCardBody>
                    </Accordion.Collapse>
                  </Card>
                ))}
              </Accordion>
            </CardWrapper>
          )}
        </div>
      </SlideOut>
    </div>
  );
};

const IndentedCardBody = styled(Card.Body)`
  padding-left: 2rem;
  padding-top: 1.25rem;
  padding-bottom: 1.25rem;
`;
const CardWrapper = styled.div`
  overflow: auto;
  border-bottom: 1px solid rgba(0, 0, 0, 0.125);
  .accordion {
    display: flex;
    flex-direction: column;
    .card {
      border: 1px solid rgba(0, 0, 0, 0.125);
      border-bottom: 0;
    }
  }
`;
